/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { graphql, useFragment } from 'react-relay';

import { css, jsx } from '@atlaskit/css';

import type { channel_AtlassianSearchPage$key } from './__generated__/channel_AtlassianSearchPage.graphql';

/**
 * The whitespace in between aliases <!here> <!channel> is removed
 * This constant prevent the alias from being pushed together ex: "@here@channel"
 * We are using this constant rather than adding a space through CSS because it adds
 * a real space character that does not break up the text when selecting.
 * */
const SPACE = ' ';

export interface ChannelProps {
	children?: string[];
	node: channel_AtlassianSearchPage$key;
	noSpace?: boolean;
}

const channelStyle = css({
	whiteSpace: 'nowrap',
});

export const Channel: ({ children, node, noSpace }: ChannelProps) => JSX.Element | null = ({
	children,
	node,
	noSpace,
}: ChannelProps) => {
	const channelId = children?.[0];

	const data = useFragment(
		graphql`
			fragment channel_AtlassianSearchPage on SearchResultGraphDocument {
				entity {
					... on ExternalMessage {
						associatedWith {
							edges {
								node {
									entity {
										... on ExternalConversation {
											__typename
											channelId: externalId
											displayName
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		node,
	);

	const channelAssociation = data.entity?.associatedWith?.edges?.find(
		(edge) =>
			edge?.node?.entity?.__typename === 'ExternalConversation' &&
			Boolean(channelId) &&
			channelId === edge?.node?.entity?.channelId,
	);

	if (
		!channelAssociation ||
		channelAssociation?.node?.entity?.__typename !== 'ExternalConversation'
	) {
		return null;
	}

	const entity = channelAssociation?.node?.entity;

	return (
		<span css={channelStyle}>
			{noSpace ? '' : SPACE}#{entity?.displayName}
		</span>
	);
};
